import { supabase } from '@/lib/supabase';
import { PremiumBotConnectedAccount } from '@/hooks/use-premium-bot-detail-admin';

interface FetchOptions {
  retryCount?: number;
  timeout?: number;
  useCache?: boolean;
}

// Simple in-memory cache for frequently accessed data
const dataCache = new Map<string, { data: any; timestamp: number; ttl: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Enhanced cache utility
 */
const getCachedData = <T>(key: string): T | null => {
  const cached = dataCache.get(key);
  if (cached && Date.now() - cached.timestamp < cached.ttl) {
    return cached.data;
  }
  if (cached) {
    dataCache.delete(key);
  }
  return null;
};

const setCachedData = <T>(key: string, data: T, ttl: number = CACHE_TTL): void => {
  dataCache.set(key, { data, timestamp: Date.now(), ttl });
};

/**
 * Enhanced error handling with retry mechanism
 */
const executeWithRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 2,
  delayMs: number = 1000
): Promise<T> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Exponential backoff
      const delay = delayMs * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw new Error('Max retries exceeded');
};

/**
 * Optimized batch fetch for user profiles
 */
const fetchUserProfiles = async (userIds: string[]) => {
  if (userIds.length === 0) return [];
  
  const cacheKey = `users:${userIds.sort().join(',')}`;
  const cached = getCachedData<any[]>(cacheKey);
  if (cached) return cached;

  const { data, error } = await supabase
    .from('user_profiles')
    .select('user_id, display_name, display_email')
    .in('user_id', userIds);

  if (error) throw error;
  
  setCachedData(cacheKey, data || []);
  return data || [];
};

/**
 * Enhanced trading accounts fetch with improved relationships
 */
const fetchTradingAccountsWithDetails = async (tradingAccountIds: string[]) => {
  if (tradingAccountIds.length === 0) return [];

  const cacheKey = `trading_accounts:${tradingAccountIds.sort().join(',')}`;
  const cached = getCachedData<any[]>(cacheKey);
  if (cached) return cached;

  // First fetch trading accounts
  const { data: tradingAccounts, error: tradingError } = await supabase
    .from('trading_accounts')
    .select(`
      accountid_trading,
      number_account_trading,
      balance_account,
      live,
      last_balance_update,
      api_connection_id
    `)
    .in('accountid_trading', tradingAccountIds);

  if (tradingError) throw tradingError;
  if (!tradingAccounts || tradingAccounts.length === 0) return [];

  // Get unique API connection IDs
  const apiConnectionIds = [...new Set(tradingAccounts.map(ta => ta.api_connection_id).filter(Boolean))];
  
  if (apiConnectionIds.length === 0) {
    setCachedData(cacheKey, tradingAccounts);
    return tradingAccounts;
  }

  // Fetch API connections with CSP account details
  const { data: apiConnections, error: apiError } = await supabase
    .from('api_connections')
    .select(`
      id,
      api_name,
      csp_account_id,
      status,
      last_balance_sync
    `)
    .in('id', apiConnectionIds);

  if (apiError) {
    console.warn('API connections fetch failed:', apiError);
    setCachedData(cacheKey, tradingAccounts);
    return tradingAccounts;
  }

  // Get unique CSP account IDs
  const cspAccountIds = [...new Set(apiConnections?.map(ac => ac.csp_account_id).filter(Boolean) || [])];
  
  let cspAccounts: any[] = [];
  if (cspAccountIds.length > 0) {
    const { data: cspData, error: cspError } = await supabase
      .from('csp_accounts')
      .select(`
        id,
        csp_account_name,
        broker,
        prop_firm,
        status
      `)
      .in('id', cspAccountIds);

    if (cspError) {
      console.warn('CSP accounts fetch failed:', cspError);
    } else {
      cspAccounts = cspData || [];
    }
  }

  // Create lookup maps for efficient joining
  const apiConnectionsMap = new Map(apiConnections?.map(ac => [ac.id, ac]) || []);
  const cspAccountsMap = new Map(cspAccounts.map(csp => [csp.id, csp]));

  // Join the data efficiently
  const enhancedTradingAccounts = tradingAccounts.map(ta => {
    const apiConnection = apiConnectionsMap.get(ta.api_connection_id);
    const cspAccount = apiConnection ? cspAccountsMap.get(apiConnection.csp_account_id) : null;

    return {
      ...ta,
      api_connections: apiConnection ? {
        ...apiConnection,
        csp_accounts: cspAccount || null
      } : null
    };
  });

  setCachedData(cacheKey, enhancedTradingAccounts);
  return enhancedTradingAccounts;
};

/**
 * Fetch all connected accounts for a specific premium bot with enhanced data handling
 */
export const fetchPremiumBotConnectedAccounts = async (
  botId: string, 
  options: FetchOptions = {}
): Promise<PremiumBotConnectedAccount[]> => {
  const { retryCount = 2, useCache = true } = options;
  
  return executeWithRetry(async () => {
    const cacheKey = `premium_bot_accounts:${botId}`;
    if (useCache) {
      const cached = getCachedData<PremiumBotConnectedAccount[]>(cacheKey);
      if (cached) return cached;
    }

    // Get bot accounts for this specific premium bot (including both normal and shadow/reverse)
    const { data: botAccounts, error: botAccountsError } = await supabase
      .from('bot_accounts')
      .select(`
        id,
        bot_id,
        original_bot_id,
        accountid_trading,
        volume_multiplier,
        is_reverse_position,
        status,
        added_date,
        user_id
      `)
      .or(`bot_id.eq.${botId},original_bot_id.eq.${botId}`)
      .order('added_date', { ascending: false });

    if (botAccountsError) {
      console.error('Error fetching bot accounts:', botAccountsError);
      throw new Error(`Failed to fetch bot accounts: ${botAccountsError.message}`);
    }

    if (!botAccounts || botAccounts.length === 0) {
      setCachedData(cacheKey, []);
      return [];
    }

    // Extract unique IDs for batch operations
    const userIds = [...new Set(botAccounts.map(ba => ba.user_id))];
    const tradingAccountIds = [...new Set(botAccounts.map(ba => ba.accountid_trading).filter(Boolean))];

    // Parallel fetch operations for better performance
    const [users, tradingAccounts] = await Promise.all([
      fetchUserProfiles(userIds),
      fetchTradingAccountsWithDetails(tradingAccountIds)
    ]);

    // Create lookup maps for efficient data joining
    const usersMap = new Map(users.map(user => [user.user_id, user]));
    const tradingAccountsMap = new Map(tradingAccounts.map(ta => [ta.accountid_trading, ta]));

    // Transform data with enhanced processing
    const result: PremiumBotConnectedAccount[] = botAccounts.map(botAccount => {
      const user = usersMap.get(botAccount.user_id);
      const tradingAccount = tradingAccountsMap.get(botAccount.accountid_trading);
      
      // Enhanced broker/prop firm detection
      const apiConnection = tradingAccount?.api_connections;
      const cspAccount = apiConnection?.csp_accounts;
      
      // Improved reverse position detection
      const isReversePosition = botAccount.is_reverse_position || 
        (botAccount.bot_id !== botAccount.original_bot_id);

      // Enhanced broker detection with fallbacks
      let detectedBroker = cspAccount?.broker;
      let detectedPropFirm = cspAccount?.prop_firm;
      
      // Additional broker detection from API name patterns
      if (!detectedBroker && apiConnection?.api_name) {
        const apiName = apiConnection.api_name.toLowerCase();
        if (apiName.includes('ctrader')) {
          detectedBroker = 'cTrader';
        } else if (apiName.includes('mt4') || apiName.includes('metatrader4')) {
          detectedBroker = 'MetaTrader 4';
        } else if (apiName.includes('mt5') || apiName.includes('metatrader5')) {
          detectedBroker = 'MetaTrader 5';
        }
      }

      return {
        id: botAccount.id,
        botId: botAccount.bot_id,
        tradingAccountId: botAccount.accountid_trading,
        volumeMultiplier: botAccount.volume_multiplier || 1,
        isReversePosition,
        status: botAccount.status || 'active',
        addedDate: botAccount.added_date,
        // Enhanced user info
        userId: botAccount.user_id,
        userName: user?.display_name || 'Unknown User',
        userEmail: user?.display_email || 'No email',
        userAvatar: undefined,
        // Enhanced account info
        accountNumber: tradingAccount?.number_account_trading || botAccount.accountid_trading,
        balance: tradingAccount?.balance_account || null,
        isLive: tradingAccount?.live || false,
        broker: detectedBroker,
        propFirm: detectedPropFirm,
        lastBalanceUpdate: tradingAccount?.last_balance_update,
      };
    });

    setCachedData(cacheKey, result);
    return result;
  }, retryCount);
};

/**
 * Enhanced balance sync with progress tracking and better error handling
 */
export const syncPremiumBotAccountBalances = async (
  botId: string,
  options: FetchOptions = {}
): Promise<{ success: boolean; synced: number; failed: number; errors: string[] }> => {
  const { retryCount = 1 } = options;
  
  return executeWithRetry(async () => {
    const errors: string[] = [];
    let syncedCount = 0;
    let failedCount = 0;

    try {
      // Get all trading account IDs connected to this bot
      const { data: botAccounts, error: botAccountsError } = await supabase
        .from('bot_accounts')
        .select('accountid_trading, api_connection_id')
        .or(`bot_id.eq.${botId},original_bot_id.eq.${botId}`);

      if (botAccountsError) {
        throw new Error(`Failed to fetch bot accounts: ${botAccountsError.message}`);
      }

      if (!botAccounts || botAccounts.length === 0) {
        return { success: true, synced: 0, failed: 0, errors: [] };
      }

      // Get unique API connection IDs from bot accounts
      const apiConnectionIds = [...new Set(
        botAccounts
          .map(ba => ba.api_connection_id)
          .filter(Boolean)
      )];

      if (apiConnectionIds.length === 0) {
        return { success: true, synced: 0, failed: 0, errors: ['No API connections found'] };
      }

      // Batch update API connections for balance sync
      const syncPromises = apiConnectionIds.map(async (connectionId) => {
        try {
          const { error: syncError } = await supabase
            .from('api_connections')
            .update({
              last_balance_sync: new Date().toISOString(),
              balance_sync_status: 'pending'
            })
            .eq('id', connectionId);

          if (syncError) {
            errors.push(`Connection ${connectionId}: ${syncError.message}`);
            failedCount++;
          } else {
            syncedCount++;
          }
        } catch (error) {
          errors.push(`Connection ${connectionId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
          failedCount++;
        }
      });

      await Promise.allSettled(syncPromises);

      // Clear relevant caches to force fresh data on next fetch
      const cacheKeys = [`premium_bot_accounts:${botId}`];
      cacheKeys.forEach(key => dataCache.delete(key));

      return {
        success: syncedCount > 0,
        synced: syncedCount,
        failed: failedCount,
        errors
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error during sync';
      return {
        success: false,
        synced: 0,
        failed: 1,
        errors: [errorMessage]
      };
    }
  }, retryCount);
};

/**
 * Clear cache for specific bot or all cached data
 */
export const clearPremiumBotCache = (botId?: string): void => {
  if (botId) {
    // Clear specific bot cache
    const keysToDelete = Array.from(dataCache.keys()).filter(key => 
      key.includes(botId) || key.startsWith('premium_bot_accounts:')
    );
    keysToDelete.forEach(key => dataCache.delete(key));
  } else {
    // Clear all cache
    dataCache.clear();
  }
};

/**
 * Get cache statistics for monitoring
 */
export const getCacheStats = (): { totalEntries: number; cacheSize: string; oldestEntry: number } => {
  const entries = Array.from(dataCache.values());
  const totalEntries = entries.length;
  const cacheSize = `${(JSON.stringify(Array.from(dataCache.entries())).length / 1024).toFixed(2)} KB`;
  const oldestEntry = entries.length > 0 ? Math.min(...entries.map(e => e.timestamp)) : 0;
  
  return {
    totalEntries,
    cacheSize,
    oldestEntry
  };
};